export type SocialBadgeId =
  | 'nuevo'
  | 'activo'
  | 'fan'
  | 'topfan'
  | 'vip'
  | 'embajador'
  | 'moderador'
  | 'locutor'
  | 'verificado'
  | 'patrocinador'
  | 'ganador'
  | 'campeon'
  | 'leyenda';

export type VisibilityLocation = 'perfil' | 'comentarios' | 'cabina' | 'clubes' | 'rankings' | 'concursos';

export interface SocialBadge {
  id: SocialBadgeId;
  name: string;
  shortName: string;
  icon: string;
  description: string;
  howToEarn: string;
  category: 'participacion' | 'estatus' | 'logro' | 'rol';
  tier: 'basico' | 'intermedio' | 'avanzado' | 'elite';
  color: string;
  gradient: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  ringColor: string;
  glowColor: string;
  dotColor: string;
  rarity: number;
  totalHolders: number;
  visibility: VisibilityLocation[];
  exclusive: boolean;
}

export interface UserWithBadges {
  id: number;
  name: string;
  username: string;
  photoUrl: string;
  badgeIds: SocialBadgeId[];
  highlightBadge: SocialBadgeId;
  radio: string;
  radioFreq: string;
  score: number;
  joinedDate: string;
  isActive: boolean;
}

export interface BadgeDashboardStats {
  totalBadges: number;
  totalAssignments: number;
  mostCommonBadge: string;
  mostCommonBadgeCount: number;
  rarestBadge: string;
  rarestBadgeCount: number;
  exclusiveBadges: number;
  averageBadgesPerUser: number;
  usersWithBadges: number;
}

export interface VisibilityConfig {
  location: VisibilityLocation;
  label: string;
  icon: string;
  description: string;
  activeBadgeCount: number;
}

export const socialBadges: SocialBadge[] = [
  {
    id: 'nuevo',
    name: 'Nuevo',
    shortName: 'NUEVO',
    icon: 'ri-seedling-line',
    description: 'Recién llegado a la comunidad RFW. Los nuevos traen energía fresca y ganas de participar.',
    howToEarn: 'Crear tu cuenta y empezar a escuchar tu primera radio.',
    category: 'participacion',
    tier: 'basico',
    color: 'text-emerald-600',
    gradient: 'from-emerald-400 to-green-500',
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-300',
    ringColor: 'ring-emerald-300/60',
    glowColor: 'shadow-emerald-400/30',
    dotColor: 'bg-emerald-500',
    rarity: 1,
    totalHolders: 12580,
    visibility: ['perfil'],
    exclusive: false,
  },
  {
    id: 'activo',
    name: 'Activo',
    shortName: 'ACTIVO',
    icon: 'ri-flashlight-line',
    description: 'Participante constante que genera actividad diaria. Los activos mueven la plataforma.',
    howToEarn: 'Mantener 30+ días consecutivos de actividad en la plataforma.',
    category: 'participacion',
    tier: 'intermedio',
    color: 'text-sky-600',
    gradient: 'from-sky-400 to-cyan-500',
    bgColor: 'bg-sky-100',
    textColor: 'text-sky-700',
    borderColor: 'border-sky-300',
    ringColor: 'ring-sky-300/60',
    glowColor: 'shadow-sky-400/30',
    dotColor: 'bg-sky-500',
    rarity: 2,
    totalHolders: 8720,
    visibility: ['perfil', 'comentarios', 'rankings'],
    exclusive: false,
  },
  {
    id: 'fan',
    name: 'Fan',
    shortName: 'FAN',
    icon: 'ri-heart-pulse-line',
    description: 'Oyente apasionado que vibra con la música y el contenido de su radio favorita.',
    howToEarn: 'Escuchar 100+ horas, seguir 3+ radios y participar en 10+ publicaciones.',
    category: 'participacion',
    tier: 'intermedio',
    color: 'text-rose-600',
    gradient: 'from-rose-400 to-pink-500',
    bgColor: 'bg-rose-100',
    textColor: 'text-rose-700',
    borderColor: 'border-rose-300',
    ringColor: 'ring-rose-300/60',
    glowColor: 'shadow-rose-400/30',
    dotColor: 'bg-rose-500',
    rarity: 3,
    totalHolders: 6540,
    visibility: ['perfil', 'comentarios', 'clubes'],
    exclusive: false,
  },
  {
    id: 'topfan',
    name: 'Top Fan',
    shortName: 'TOP FAN',
    icon: 'ri-fire-line',
    description: 'Elite de oyentes que destacan por su actividad, engagement y fidelidad a la comunidad.',
    howToEarn: 'Rankear en el top 100 de actividad global por 3 meses consecutivos.',
    category: 'estatus',
    tier: 'avanzado',
    color: 'text-orange-600',
    gradient: 'from-orange-400 to-red-500',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-300',
    ringColor: 'ring-orange-300/60',
    glowColor: 'shadow-orange-400/30',
    dotColor: 'bg-orange-500',
    rarity: 4,
    totalHolders: 2850,
    visibility: ['perfil', 'comentarios', 'cabina', 'clubes', 'rankings'],
    exclusive: false,
  },
  {
    id: 'vip',
    name: 'VIP',
    shortName: 'VIP',
    icon: 'ri-vip-diamond-line',
    description: 'Miembro del círculo más exclusivo de RFW. Acceso a contenido, eventos y concursos premium.',
    howToEarn: 'Alcanzar 50K puntos, 90 días de actividad, invitación de Embajador o suscripción.',
    category: 'estatus',
    tier: 'avanzado',
    color: 'text-violet-600',
    gradient: 'from-violet-400 via-purple-500 to-fuchsia-500',
    bgColor: 'bg-violet-100',
    textColor: 'text-violet-700',
    borderColor: 'border-violet-300',
    ringColor: 'ring-violet-300/60',
    glowColor: 'shadow-violet-400/30',
    dotColor: 'bg-violet-500',
    rarity: 5,
    totalHolders: 1250,
    visibility: ['perfil', 'comentarios', 'cabina', 'clubes', 'rankings', 'concursos'],
    exclusive: true,
  },
  {
    id: 'embajador',
    name: 'Embajador',
    shortName: 'EMBAJADOR',
    icon: 'ri-vip-crown-line',
    description: 'Promotor oficial de RFW. Representa la plataforma, invita nuevos oyentes y construye comunidad.',
    howToEarn: 'Ser seleccionado por RFW tras demostrar liderazgo comunitario y 15K+ puntos.',
    category: 'rol',
    tier: 'elite',
    color: 'text-amber-600',
    gradient: 'from-amber-400 to-yellow-500',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-300',
    ringColor: 'ring-amber-300/60',
    glowColor: 'shadow-amber-400/30',
    dotColor: 'bg-amber-500',
    rarity: 6,
    totalHolders: 620,
    visibility: ['perfil', 'comentarios', 'cabina', 'clubes', 'rankings', 'concursos'],
    exclusive: true,
  },
  {
    id: 'moderador',
    name: 'Moderador',
    shortName: 'MOD',
    icon: 'ri-shield-check-line',
    description: 'Guardián de la comunidad. Mantiene el orden en cabinas y clubes con criterio y justicia.',
    howToEarn: 'Ser designado por el dueño de una radio o el equipo RFW tras demostrar criterio.',
    category: 'rol',
    tier: 'avanzado',
    color: 'text-teal-600',
    gradient: 'from-teal-400 to-emerald-500',
    bgColor: 'bg-teal-100',
    textColor: 'text-teal-700',
    borderColor: 'border-teal-300',
    ringColor: 'ring-teal-300/60',
    glowColor: 'shadow-teal-400/30',
    dotColor: 'bg-teal-500',
    rarity: 4,
    totalHolders: 840,
    visibility: ['perfil', 'comentarios', 'cabina', 'clubes'],
    exclusive: false,
  },
  {
    id: 'locutor',
    name: 'Locutor',
    shortName: 'LOCUTOR',
    icon: 'ri-mic-line',
    description: 'La voz detrás del micrófono. Creador de contenido que transmite en vivo para su audiencia.',
    howToEarn: 'Ser locutor activo de una radio registrada en la plataforma RFW.',
    category: 'rol',
    tier: 'avanzado',
    color: 'text-indigo-600',
    gradient: 'from-indigo-400 to-blue-500',
    bgColor: 'bg-indigo-100',
    textColor: 'text-indigo-700',
    borderColor: 'border-indigo-300',
    ringColor: 'ring-indigo-300/60',
    glowColor: 'shadow-indigo-400/30',
    dotColor: 'bg-indigo-500',
    rarity: 4,
    totalHolders: 1560,
    visibility: ['perfil', 'comentarios', 'cabina', 'clubes', 'rankings'],
    exclusive: false,
  },
  {
    id: 'verificado',
    name: 'Verificado',
    shortName: 'VERIF.',
    icon: 'ri-verified-badge-line',
    description: 'Cuenta verificada oficialmente por RFW. Autenticidad y confianza garantizada.',
    howToEarn: 'Solicitar verificación y ser aprobado por el equipo RFW tras comprobación de identidad.',
    category: 'estatus',
    tier: 'intermedio',
    color: 'text-blue-600',
    gradient: 'from-blue-400 to-cyan-500',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-300',
    ringColor: 'ring-blue-300/60',
    glowColor: 'shadow-blue-400/30',
    dotColor: 'bg-blue-500',
    rarity: 3,
    totalHolders: 3240,
    visibility: ['perfil', 'comentarios', 'cabina', 'clubes', 'rankings', 'concursos'],
    exclusive: false,
  },
  {
    id: 'patrocinador',
    name: 'Patrocinador',
    shortName: 'PATROC.',
    icon: 'ri-hand-heart-line',
    description: 'Marca o empresa que apoya concursos, eventos y contenido en la plataforma RFW.',
    howToEarn: 'Patrocinar un concurso, evento o contenido en cualquier radio de la red RFW.',
    category: 'rol',
    tier: 'elite',
    color: 'text-slate-600',
    gradient: 'from-slate-400 to-zinc-500',
    bgColor: 'bg-slate-100',
    textColor: 'text-slate-700',
    borderColor: 'border-slate-300',
    ringColor: 'ring-slate-300/60',
    glowColor: 'shadow-slate-400/30',
    dotColor: 'bg-slate-500',
    rarity: 5,
    totalHolders: 480,
    visibility: ['perfil', 'comentarios', 'concursos'],
    exclusive: false,
  },
  {
    id: 'ganador',
    name: 'Ganador',
    shortName: 'GANADOR',
    icon: 'ri-trophy-line',
    description: 'Ha ganado concursos en la plataforma. Los ganadores inspiran a la comunidad a participar.',
    howToEarn: 'Ganar un concurso oficial en cualquier radio de la red RFW.',
    category: 'logro',
    tier: 'intermedio',
    color: 'text-yellow-600',
    gradient: 'from-yellow-400 to-amber-500',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-700',
    borderColor: 'border-yellow-300',
    ringColor: 'ring-yellow-300/60',
    glowColor: 'shadow-yellow-400/30',
    dotColor: 'bg-yellow-500',
    rarity: 3,
    totalHolders: 4120,
    visibility: ['perfil', 'comentarios', 'rankings', 'concursos'],
    exclusive: false,
  },
  {
    id: 'campeon',
    name: 'Campeón',
    shortName: 'CAMPEÓN',
    icon: 'ri-medal-line',
    description: 'Alcanzó la cima en competencias y copas RFW. Los campeones son leyendas vivientes.',
    howToEarn: 'Ganar una Copa RFW o finalizar #1 en una competencia oficial de la plataforma.',
    category: 'logro',
    tier: 'elite',
    color: 'text-red-600',
    gradient: 'from-red-400 to-rose-500',
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    borderColor: 'border-red-300',
    ringColor: 'ring-red-300/60',
    glowColor: 'shadow-red-400/30',
    dotColor: 'bg-red-500',
    rarity: 6,
    totalHolders: 380,
    visibility: ['perfil', 'comentarios', 'cabina', 'clubes', 'rankings', 'concursos'],
    exclusive: true,
  },
  {
    id: 'leyenda',
    name: 'Leyenda',
    shortName: 'LEYENDA',
    icon: 'ri-star-smile-line',
    description: 'El máximo reconocimiento en RFW. Trayectoria impecable, comunidad fiel y legado eterno.',
    howToEarn: 'Acumular 10+ años de actividad, 100K+ puntos, múltiples copas y embajadas.',
    category: 'logro',
    tier: 'elite',
    color: 'text-fuchsia-600',
    gradient: 'from-amber-400 via-rose-400 via-purple-400 to-indigo-400',
    bgColor: 'bg-fuchsia-100',
    textColor: 'text-fuchsia-700',
    borderColor: 'border-fuchsia-300',
    ringColor: 'ring-fuchsia-300/60',
    glowColor: 'shadow-fuchsia-400/40',
    dotColor: 'bg-fuchsia-500',
    rarity: 7,
    totalHolders: 85,
    visibility: ['perfil', 'comentarios', 'cabina', 'clubes', 'rankings', 'concursos'],
    exclusive: true,
  },
];

export const visibilityConfigs: VisibilityConfig[] = [
  { location: 'perfil', label: 'Perfil', icon: 'ri-user-line', description: 'Visible en la página de perfil del usuario junto a su nombre y foto.', activeBadgeCount: 13 },
  { location: 'comentarios', label: 'Comentarios', icon: 'ri-chat-1-line', description: 'Aparece junto al nombre en publicaciones, fotos y videos.', activeBadgeCount: 11 },
  { location: 'cabina', label: 'Cabina', icon: 'ri-message-2-line', description: 'Visible en el chat en vivo de la cabina durante las transmisiones.', activeBadgeCount: 8 },
  { location: 'clubes', label: 'Clubes', icon: 'ri-group-line', description: 'Se muestra en la lista de miembros de clubes de fans.', activeBadgeCount: 9 },
  { location: 'rankings', label: 'Rankings', icon: 'ri-bar-chart-line', description: 'Visible en todas las tablas de posiciones y leaderboards.', activeBadgeCount: 8 },
  { location: 'concursos', label: 'Concursos', icon: 'ri-trophy-line', description: 'Aparece en inscripciones, resultados y tablas de concursos.', activeBadgeCount: 7 },
];

export const usersWithBadges: UserWithBadges[] = [
  {
    id: 1, name: 'Isabella Torres', username: '@isabellatorres', photoUrl: 'https://readdy.ai/api/search-image?query=Confident%20latina%20woman%20late%20twenties%20sophisticated%20elegant%20style%20warm%20genuine%20smile%20diamond%20jewelry%20detail%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-isabella&orientation=squarish',
    badgeIds: ['vip', 'topfan', 'fan', 'activo', 'verificado', 'ganador', 'campeon'],
    highlightBadge: 'vip', radio: 'Radio Élite Digital', radioFreq: '99.1 FM', score: 98500, joinedDate: '2022-11-08', isActive: true,
  },
  {
    id: 2, name: 'Felipe Gutiérrez', username: '@felipegutierrez', photoUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20latino%20man%20late%20twenties%20sharp%20casual%20blazer%20style%20confident%20warm%20expression%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-felipe&orientation=squarish',
    badgeIds: ['vip', 'topfan', 'fan', 'activo', 'verificado', 'ganador'],
    highlightBadge: 'topfan', radio: 'Estéreo Capital', radioFreq: '102.1 FM', score: 89200, joinedDate: '2022-06-22', isActive: true,
  },
  {
    id: 3, name: 'Adriana Mejía', username: '@adrianamejia', photoUrl: 'https://readdy.ai/api/search-image?query=Warm%20nostalgic%20latina%20woman%20late%20twenties%20elegant%20casual%20look%20artistic%20flair%20genuine%20gentle%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-adriana&orientation=squarish',
    badgeIds: ['vip', 'fan', 'activo', 'embajador', 'verificado', 'ganador'],
    highlightBadge: 'embajador', radio: 'Radio Peninsular', radioFreq: '101.9 FM', score: 81100, joinedDate: '2022-09-14', isActive: true,
  },
  {
    id: 4, name: 'Javier Mendoza', username: '@javiermendoza', photoUrl: 'https://readdy.ai/api/search-image?query=Sporty%20energetic%20latino%20man%20late%20twenties%20athletic%20casual%20style%20confident%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-javier&orientation=squarish',
    badgeIds: ['vip', 'topfan', 'fan', 'activo', 'verificado', 'campeon'],
    highlightBadge: 'campeon', radio: 'Radio Deportiva Total', radioFreq: '95.7 FM', score: 91200, joinedDate: '2022-03-15', isActive: true,
  },
  {
    id: 5, name: 'Camila Restrepo', username: '@camilarestrepo', photoUrl: 'https://readdy.ai/api/search-image?query=Friendly%20latina%20woman%20late%20twenties%20warm%20urban%20casual%20style%20bright%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-camila&orientation=squarish',
    badgeIds: ['vip', 'fan', 'activo', 'ganador', 'nuevo'],
    highlightBadge: 'ganador', radio: 'Radio Éxito FM', radioFreq: '98.5 FM', score: 75200, joinedDate: '2022-12-01', isActive: true,
  },
  {
    id: 6, name: 'Ricky Navarro', username: '@rickynavarro', photoUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20latino%20man%20late%20twenties%20radio%20host%20casual%20vibe%20warm%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-ricky&orientation=squarish',
    badgeIds: ['locutor', 'fan', 'activo', 'verificado', 'moderador'],
    highlightBadge: 'locutor', radio: 'Radio Élite Digital', radioFreq: '99.1 FM', score: 68900, joinedDate: '2023-02-18', isActive: true,
  },
  {
    id: 7, name: 'Valentina Ríos', username: '@valentinarios', photoUrl: 'https://readdy.ai/api/search-image?query=Trendy%20latina%20woman%20mid%20twenties%20fashionable%20streetwear%20style%20bright%20confident%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-valentina&orientation=squarish',
    badgeIds: ['vip', 'fan', 'activo', 'verificado', 'ganador', 'nuevo'],
    highlightBadge: 'fan', radio: 'Radio Juvenil FM', radioFreq: '105.3 FM', score: 72500, joinedDate: '2023-04-22', isActive: true,
  },
  {
    id: 8, name: 'Gabriel Paredes', username: '@gabrielparedes', photoUrl: 'https://readdy.ai/api/search-image?query=Intellectual%20latino%20man%20late%20twenties%20refined%20casual%20style%20glasses%20warm%20thoughtful%20expression%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-gabriel&orientation=squarish',
    badgeIds: ['vip', 'activo', 'verificado', 'patrocinador'],
    highlightBadge: 'patrocinador', radio: 'Radio Industria', radioFreq: '93.5 FM', score: 65800, joinedDate: '2023-01-05', isActive: true,
  },
  {
    id: 9, name: 'Laura Santamaría', username: '@laurasantamaria', photoUrl: 'https://readdy.ai/api/search-image?query=Elegant%20latina%20woman%20mid%20twenties%20classic%20refined%20style%20warm%20genuine%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-laura&orientation=squarish',
    badgeIds: ['fan', 'activo', 'ganador', 'nuevo'],
    highlightBadge: 'activo', radio: 'Radio Éxito FM', radioFreq: '98.5 FM', score: 58200, joinedDate: '2023-10-08', isActive: true,
  },
  {
    id: 10, name: 'Daniel Ortega', username: '@danielortega', photoUrl: 'https://readdy.ai/api/search-image?query=Relaxed%20latino%20man%20mid%20twenties%20casual%20tshirt%20style%20easygoing%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-daniel&orientation=squarish',
    badgeIds: ['nuevo', 'activo', 'fan'],
    highlightBadge: 'nuevo', radio: 'Estéreo Capital', radioFreq: '102.1 FM', score: 54500, joinedDate: '2023-11-15', isActive: true,
  },
  {
    id: 11, name: 'Paola Rincón', username: '@paolarincon', photoUrl: 'https://readdy.ai/api/search-image?query=Sporty%20casual%20latina%20woman%20mid%20twenties%20confident%20warm%20smile%20athletic%20streetwear%20style%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-paola&orientation=squarish',
    badgeIds: ['vip', 'fan', 'activo', 'ganador', 'verificado'],
    highlightBadge: 'fan', radio: 'Radio Deportiva Total', radioFreq: '95.7 FM', score: 61200, joinedDate: '2023-04-10', isActive: true,
  },
  {
    id: 12, name: 'María José Salazar', username: '@majosalazar', photoUrl: 'https://readdy.ai/api/search-image?query=Bright%20friendly%20latina%20woman%20mid%20twenties%20trendy%20casual%20look%20warm%20open%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-majose&orientation=squarish',
    badgeIds: ['fan', 'activo', 'moderador', 'verificado', 'leyenda'],
    highlightBadge: 'leyenda', radio: 'Estéreo Capital', radioFreq: '102.1 FM', score: 57800, joinedDate: '2016-03-22', isActive: true,
  },
  {
    id: 13, name: 'Carlos Herrera', username: '@carlosherrera', photoUrl: 'https://readdy.ai/api/search-image?query=Friendly%20latino%20man%20mid%20twenties%20clean%20casual%20style%20warm%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-carlos&orientation=squarish',
    badgeIds: ['nuevo', 'activo'],
    highlightBadge: 'activo', radio: 'Radio Deportiva Total', radioFreq: '95.7 FM', score: 52100, joinedDate: '2024-01-20', isActive: true,
  },
  {
    id: 14, name: 'Sofía Quintero', username: '@sofiaquintero', photoUrl: 'https://readdy.ai/api/search-image?query=Warm%20genuine%20latina%20woman%20mid%20twenties%20relaxed%20boho%20style%20soft%20natural%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-sofiaq&orientation=squarish',
    badgeIds: ['fan', 'activo', 'nuevo'],
    highlightBadge: 'fan', radio: 'Radio Éxito FM', radioFreq: '98.5 FM', score: 49800, joinedDate: '2024-10-02', isActive: true,
  },
  {
    id: 15, name: 'Andrés Murillo', username: '@andresmurillo', photoUrl: 'https://readdy.ai/api/search-image?query=Confident%20latino%20man%20mid%20twenties%20casual%20hoodie%20street%20style%20warm%20engaging%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-andres&orientation=squarish',
    badgeIds: ['fan', 'activo', 'topfan', 'ganador'],
    highlightBadge: 'topfan', radio: 'Radio Éxito FM', radioFreq: '98.5 FM', score: 50800, joinedDate: '2023-06-15', isActive: true,
  },
  {
    id: 16, name: 'Natalia Córdoba', username: '@nataliacordoba', photoUrl: 'https://readdy.ai/api/search-image?query=Professional%20elegant%20latina%20woman%20late%20twenties%20polished%20corporate%20casual%20style%20warm%20confident%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-natalia&orientation=squarish',
    badgeIds: ['activo', 'verificado', 'moderador'],
    highlightBadge: 'moderador', radio: 'Radio Industria', radioFreq: '93.5 FM', score: 48500, joinedDate: '2023-09-20', isActive: true,
  },
  {
    id: 17, name: 'Roberto Silva', username: '@robertosilva', photoUrl: 'https://readdy.ai/api/search-image?query=Warm%20classic%20latino%20man%20mid%20twenties%20friendly%20genuine%20smile%20smart%20casual%20style%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-roberto&orientation=squarish',
    badgeIds: ['nuevo', 'fan'],
    highlightBadge: 'nuevo', radio: 'Radio Peninsular', radioFreq: '101.9 FM', score: 46200, joinedDate: '2025-02-14', isActive: true,
  },
  {
    id: 18, name: 'Ana Lucía Vargas', username: '@analuvargas', photoUrl: 'https://readdy.ai/api/search-image?query=Fresh%20natural%20latina%20woman%20mid%20twenties%20casual%20boho%20chic%20style%20warm%20radiant%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-analu&orientation=squarish',
    badgeIds: ['vip', 'fan', 'activo', 'ganador', 'verificado'],
    highlightBadge: 'vip', radio: 'Radio Éxito FM', radioFreq: '98.5 FM', score: 51800, joinedDate: '2024-08-05', isActive: true,
  },
  {
    id: 19, name: 'Valeria Duque', username: '@valeriaduque', photoUrl: 'https://readdy.ai/api/search-image?query=Creative%20artsy%20latina%20woman%20mid%20twenties%20artistic%20casual%20style%20paint%20details%20warm%20open%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-valeria&orientation=squarish',
    badgeIds: ['fan', 'activo', 'nuevo'],
    highlightBadge: 'activo', radio: 'Radio Élite Digital', radioFreq: '99.1 FM', score: 47800, joinedDate: '2025-04-05', isActive: true,
  },
  {
    id: 20, name: 'Santiago López', username: '@santilopez', photoUrl: 'https://readdy.ai/api/search-image?query=Tech%20casual%20latino%20man%20mid%20twenties%20smart%20modern%20style%20laptop%20bag%20friendly%20geeky%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-santiago&orientation=squarish',
    badgeIds: ['nuevo', 'activo', 'fan', 'locutor'],
    highlightBadge: 'locutor', radio: 'Radio Élite Digital', radioFreq: '99.1 FM', score: 45200, joinedDate: '2023-08-02', isActive: true,
  },
  {
    id: 21, name: 'Carmen Díaz', username: '@carmendiaz', photoUrl: 'https://readdy.ai/api/search-image?query=Vintage%20chic%20latina%20woman%20late%20twenties%20retro%20elegant%20style%20warm%20expressive%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-carmen&orientation=squarish',
    badgeIds: ['nuevo', 'activo'],
    highlightBadge: 'nuevo', radio: 'Radio Peninsular', radioFreq: '101.9 FM', score: 43500, joinedDate: '2025-06-22', isActive: true,
  },
  {
    id: 22, name: 'Mateo Álvarez', username: '@mateoalvarez', photoUrl: 'https://readdy.ai/api/search-image?query=Serious%20engaged%20latino%20man%20mid%20twenties%20neat%20casual%20style%20glasses%20intellectual%20expression%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-mateo&orientation=squarish',
    badgeIds: ['nuevo', 'fan', 'activo'],
    highlightBadge: 'fan', radio: 'Radio Industria', radioFreq: '93.5 FM', score: 42200, joinedDate: '2025-05-18', isActive: true,
  },
  {
    id: 23, name: 'Diego Ramírez', username: '@diegoramirez', photoUrl: 'https://readdy.ai/api/search-image?query=Casual%20relaxed%20latino%20man%20mid%20twenties%20easygoing%20style%20warm%20natural%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-diego&orientation=squarish',
    badgeIds: ['nuevo'],
    highlightBadge: 'nuevo', radio: 'Estéreo Capital', radioFreq: '102.1 FM', score: 40800, joinedDate: '2025-07-10', isActive: true,
  },
  {
    id: 24, name: 'Tomás Navarro', username: '@tomasnavarro', photoUrl: 'https://readdy.ai/api/search-image?query=Curious%20enthusiastic%20latino%20man%20early%20twenties%20casual%20hoodie%20style%20bright%20eyes%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-tomas&orientation=squarish',
    badgeIds: ['nuevo', 'activo', 'fan', 'vip'],
    highlightBadge: 'vip', radio: 'Radio Deportiva Total', radioFreq: '95.7 FM', score: 39500, joinedDate: '2024-09-12', isActive: true,
  },
  {
    id: 25, name: 'Mariana Duque', username: '@marianaduque', photoUrl: 'https://readdy.ai/api/search-image?query=Sophisticated%20latina%20woman%20early%20twenties%20artistic%20elegant%20style%20warm%20subtle%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-mariana&orientation=squarish',
    badgeIds: ['nuevo', 'activo', 'fan', 'topfan', 'verificado'],
    highlightBadge: 'verificado', radio: 'Estéreo Capital', radioFreq: '92.1 FM', score: 54200, joinedDate: '2024-05-28', isActive: true,
  },
  {
    id: 26, name: 'Julián Paredes', username: '@julianparedes', photoUrl: 'https://readdy.ai/api/search-image?query=Determined%20latino%20man%20mid%20twenties%20sporty%20professional%20style%20focused%20expression%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-julian&orientation=squarish',
    badgeIds: ['nuevo', 'activo', 'fan'],
    highlightBadge: 'fan', radio: 'Radio Deportiva Total', radioFreq: '95.7 FM', score: 38800, joinedDate: '2025-08-18', isActive: true,
  },
  {
    id: 27, name: 'Carolina Estévez', username: '@carolinaestevez', photoUrl: 'https://readdy.ai/api/search-image?query=Sophisticated%20warm%20latina%20woman%20early%20twenties%20elegant%20casual%20look%20refined%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-carolina&orientation=squarish',
    badgeIds: ['nuevo', 'activo', 'fan', 'vip'],
    highlightBadge: 'vip', radio: 'Radio Élite Digital', radioFreq: '99.1 FM', score: 37500, joinedDate: '2025-02-22', isActive: true,
  },
  {
    id: 28, name: 'Beatriz Herrera', username: '@beatrizherrera', photoUrl: 'https://readdy.ai/api/search-image?query=Classic%20refined%20latina%20woman%20late%20twenties%20cultured%20elegant%20style%20warm%20intellectual%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-beatriz&orientation=squarish',
    badgeIds: ['activo', 'fan', 'topfan', 'verificado', 'ganador'],
    highlightBadge: 'topfan', radio: 'Radio Clásica', radioFreq: '101.5 FM', score: 41200, joinedDate: '2024-03-15', isActive: true,
  },
  {
    id: 29, name: 'Kevin Toro', username: '@kevintoro', photoUrl: 'https://readdy.ai/api/search-image?query=Creative%20urban%20latino%20man%20early%20twenties%20music%20producer%20style%20headphones%20energetic%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-kevin&orientation=squarish',
    badgeIds: ['activo', 'fan', 'ganador'],
    highlightBadge: 'ganador', radio: 'Radio Juvenil FM', radioFreq: '105.3 FM', score: 46800, joinedDate: '2025-06-05', isActive: true,
  },
  {
    id: 30, name: 'Catalina Muñoz', username: '@catalinamunoz', photoUrl: 'https://readdy.ai/api/search-image?query=Artistic%20latina%20woman%20early%20twenties%20alternative%20rock%20style%20warm%20expressive%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=200&height=200&seq=badge-user-catalina&orientation=squarish',
    badgeIds: ['nuevo', 'activo', 'fan', 'moderador'],
    highlightBadge: 'moderador', radio: 'Radio Rock 360', radioFreq: '96.8 FM', score: 39800, joinedDate: '2025-04-22', isActive: true,
  },
];

export const badgeDashboardStats: BadgeDashboardStats = (() => {
  const totalAssignments = usersWithBadges.reduce((s, u) => s + u.badgeIds.length, 0);
  const badgeCounts = new Map<SocialBadgeId, number>();
  usersWithBadges.forEach((u) => {
    u.badgeIds.forEach((bid) => {
      badgeCounts.set(bid, (badgeCounts.get(bid) || 0) + 1);
    });
  });

  let mostCommon: [string, number] = ['', 0];
  let rarest: [string, number] = ['', Infinity];
  badgeCounts.forEach((count, bid) => {
    if (count > mostCommon[1]) mostCommon = [bid, count];
    if (count < rarest[1]) rarest = [bid, count];
  });

  const mostCommonBadge = socialBadges.find((b) => b.id === mostCommon[0]);
  const rarestBadge = socialBadges.find((b) => b.id === rarest[0]);

  return {
    totalBadges: socialBadges.length,
    totalAssignments,
    mostCommonBadge: mostCommonBadge?.name || '',
    mostCommonBadgeCount: mostCommon[1],
    rarestBadge: rarestBadge?.name || '',
    rarestBadgeCount: rarest[1],
    exclusiveBadges: socialBadges.filter((b) => b.exclusive).length,
    averageBadgesPerUser: Math.round((totalAssignments / usersWithBadges.length) * 10) / 10,
    usersWithBadges: usersWithBadges.length,
  };
})();

export function getBadgeById(id: SocialBadgeId): SocialBadge | undefined {
  return socialBadges.find((b) => b.id === id);
}

export function getBadgesForUser(userId: number): SocialBadge[] {
  const user = usersWithBadges.find((u) => u.id === userId);
  if (!user) return [];
  return user.badgeIds.map((bid) => getBadgeById(bid)).filter((b): b is SocialBadge => b !== undefined);
}

export function getUsersWithBadge(badgeId: SocialBadgeId): UserWithBadges[] {
  return usersWithBadges.filter((u) => u.badgeIds.includes(badgeId));
}

export function getBadgesByCategory(category: SocialBadge['category']): SocialBadge[] {
  return socialBadges.filter((b) => b.category === category);
}

export function getBadgesByTier(tier: SocialBadge['tier']): SocialBadge[] {
  return socialBadges.filter((b) => b.tier === tier);
}

export function getFeaturedBadges(limit = 8): SocialBadge[] {
  return [...socialBadges].sort((a, b) => b.rarity - a.rarity).slice(0, limit);
}

export function getUsersByHighlightBadge(badgeId: SocialBadgeId, limit = 3): UserWithBadges[] {
  return usersWithBadges.filter((u) => u.highlightBadge === badgeId).slice(0, limit);
}

export function getTopBadgedUsers(limit = 10): UserWithBadges[] {
  return [...usersWithBadges].sort((a, b) => b.badgeIds.length - a.badgeIds.length || b.score - a.score).slice(0, limit);
}

export function getVisibilityBadges(location: VisibilityLocation): SocialBadge[] {
  return socialBadges.filter((b) => b.visibility.includes(location));
}

export const categoryTiers: Record<SocialBadge['category'], { label: string; icon: string; color: string }> = {
  participacion: { label: 'Participación', icon: 'ri-flashlight-line', color: 'text-emerald-600' },
  estatus: { label: 'Estatus', icon: 'ri-shield-star-line', color: 'text-violet-600' },
  logro: { label: 'Logro', icon: 'ri-trophy-line', color: 'text-amber-600' },
  rol: { label: 'Rol', icon: 'ri-user-star-line', color: 'text-indigo-600' },
};

export const tierConfig: Record<SocialBadge['tier'], { label: string; icon: string; color: string; bgColor: string }> = {
  basico: { label: 'Básico', icon: 'ri-circle-line', color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
  intermedio: { label: 'Intermedio', icon: 'ri-circle-fill', color: 'text-sky-600', bgColor: 'bg-sky-50' },
  avanzado: { label: 'Avanzado', icon: 'ri-star-line', color: 'text-violet-600', bgColor: 'bg-violet-50' },
  elite: { label: 'Élite', icon: 'ri-star-fill', color: 'text-fuchsia-600', bgColor: 'bg-fuchsia-50' },
};